import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import usaMap from "@svg-maps/usa";
import { useInView } from "@/hooks/useInView";

type MapLocation = { id: string; name: string; path: string };

const map = (usaMap as unknown as { default: { viewBox: string; locations: MapLocation[] } }).default
  ?? (usaMap as unknown as { viewBox: string; locations: MapLocation[] });

// Tiny states get external labels with leader lines (centers overlap neighbors).
const EXTERNAL_LABELS = new Set(["ri", "ct", "nj", "de", "md", "dc", "ma", "nh", "vt"]);
const EXTERNAL_OFFSETS: Record<string, [number, number]> = {
  vt: [40, -60],
  nh: [55, -40],
  ma: [70, -10],
  ri: [80, 20],
  ct: [70, 45],
  nj: [70, 65],
  de: [85, 85],
  md: [110, 100],
  dc: [125, 120],
};

const POPULAR_STATES = [
  "California", "Texas", "Florida", "New York", "Pennsylvania",
  "Illinois", "Ohio", "Georgia", "North Carolina", "Michigan",
];

export function MapSearchSection() {
  const { ref, inView } = useInView<HTMLDivElement>();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [centroids, setCentroids] = useState<Record<string, { x: number; y: number }>>({});
  const pathRefs = useRef<Record<string, SVGPathElement | null>>({});
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const next: Record<string, { x: number; y: number }> = {};
    for (const loc of map.locations) {
      const el = pathRefs.current[loc.id];
      if (!el) continue;
      const b = el.getBBox();
      next[loc.id] = { x: b.x + b.width / 2, y: b.y + b.height / 2 };
    }
    setCentroids(next);
  }, []);

  const active = selected ? map.locations.find((l) => l.id === selected) ?? null : null;
  const hoveredLoc = hovered ? map.locations.find((l) => l.id === hovered) ?? null : null;

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return map.locations.filter((l) => l.name.toLowerCase().includes(q)).slice(0, 6);
  }, [query]);

  const popular = useMemo(
    () => POPULAR_STATES.map((name) => map.locations.find((l) => l.name === name)).filter(Boolean) as MapLocation[],
    [],
  );

  const goToState = (loc: { id: string; name: string }) => {
    setSelected(loc.id);
    setQuery("");
    navigate({ to: "/search", search: { location: loc.name } as any });
  };

  const hoveredCentroid = hovered ? centroids[hovered] : null;
  const tooltipPos = useMemo(() => {
    if (!hoveredCentroid || !svgRef.current || !wrapRef.current) return null;
    const svgRect = svgRef.current.getBoundingClientRect();
    const wrapRect = wrapRef.current.getBoundingClientRect();
    const [vbX, vbY, vbW, vbH] = map.viewBox.split(/\s+/).map(Number);
    const sx = svgRect.width / vbW;
    const sy = svgRect.height / vbH;
    const x = svgRect.left - wrapRect.left + (hoveredCentroid.x - vbX) * sx;
    const y = svgRect.top - wrapRect.top + (hoveredCentroid.y - vbY) * sy;
    return { x, y };
  }, [hoveredCentroid]);

  return (
    <section className="relative z-10 px-6 py-10">
      <div
        ref={ref}
        style={{ transitionDelay: "60ms" }}
        className={`mx-auto max-w-[900px] transition-all duration-700 ease-out ${
          inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="relative z-10 rounded-md card-surface-flat overflow-hidden shadow-sm flex flex-col">
          {/* Card header — caps label + subtitle + inline state search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
            <div className="flex flex-col">
              <span className="text-[11px] font-semibold text-primary tracking-[0.18em] uppercase">
                Find Providers by State
              </span>
              <span className="text-[13px] text-muted-foreground mt-0.5">
                {active
                  ? `Showing ${active.name}`
                  : "Hover a state to explore — click to view its providers"}
              </span>
            </div>
            <div className="relative w-full sm:w-[280px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" aria-hidden />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a state name"
                aria-label="Search by state"
                className="w-full h-9 pl-8 pr-3 text-[13px] bg-background border border-border text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 rounded-md transition-colors"
              />
              {suggestions.length > 0 && (
                <ul className="absolute z-20 left-0 right-0 top-full mt-1 card-surface max-h-64 overflow-auto rounded-md text-left">
                  {suggestions.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        onClick={() => goToState(s)}
                        className="w-full text-left px-3 py-2 text-[13px] text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                      >
                        {s.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="p-4 relative bg-gradient-to-b from-card to-primary/5" ref={wrapRef}>
            <svg
              ref={svgRef}
              viewBox={map.viewBox}
              xmlns="http://www.w3.org/2000/svg"
              className="w-full sm:w-[85%] mx-auto h-auto overflow-visible"
              role="img"
              aria-label="Clickable map of the United States"
            >
              {map.locations.map((loc) => {
                const isHovered = hovered === loc.id;
                const isSelected = selected === loc.id;
                const c = centroids[loc.id];
                const transform =
                  isHovered && c
                    ? `translate(${c.x} ${c.y}) scale(1.08) translate(${-c.x} ${-c.y})`
                    : undefined;
                return (
                  <path
                    key={loc.id}
                    ref={(el) => {
                      pathRefs.current[loc.id] = el;
                    }}
                    d={loc.path}
                    name={loc.name}
                    aria-label={`Find providers in ${loc.name}`}
                    role="button"
                    tabIndex={0}
                    transform={transform}
                    onMouseEnter={() => setHovered(loc.id)}
                    onMouseLeave={() => setHovered((h) => (h === loc.id ? null : h))}
                    onFocus={() => setHovered(loc.id)}
                    onBlur={() => setHovered((h) => (h === loc.id ? null : h))}
                    onClick={() => goToState({ id: loc.id, name: loc.name })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goToState({ id: loc.id, name: loc.name });
                      }
                    }}
                    style={{
                      fill: isSelected
                        ? "var(--color-primary)"
                        : isHovered
                          ? "color-mix(in oklab, var(--color-primary) 65%, white)"
                          : "color-mix(in oklab, var(--color-primary) 18%, var(--color-background))",
                      stroke: "var(--color-background)",
                      strokeWidth: isHovered ? 1.4 : 1,
                      filter: isHovered
                        ? "drop-shadow(0 6px 14px color-mix(in oklab, var(--color-primary) 45%, transparent))"
                        : undefined,
                      cursor: "pointer",
                      outline: "none",
                      transition:
                        "fill 200ms ease, transform 220ms cubic-bezier(0.22,1,0.36,1), stroke-width 160ms ease, filter 220ms ease",
                    }}
                  />
                );
              })}

              {map.locations.map((loc) => {
                if (!EXTERNAL_LABELS.has(loc.id)) return null;
                const c = centroids[loc.id];
                const off = EXTERNAL_OFFSETS[loc.id];
                if (!c || !off) return null;
                return (
                  <line
                    key={`lead-${loc.id}`}
                    x1={c.x}
                    y1={c.y}
                    x2={c.x + off[0] - 2}
                    y2={c.y + off[1]}
                    stroke="color-mix(in oklab, var(--color-foreground) 40%, transparent)"
                    strokeWidth={0.6}
                    pointerEvents="none"
                  />
                );
              })}

              {map.locations.map((loc) => {
                const c = centroids[loc.id];
                if (!c) return null;
                const abbr = loc.id.toUpperCase();
                const isExternal = EXTERNAL_LABELS.has(loc.id);
                const off = isExternal ? EXTERNAL_OFFSETS[loc.id] : null;
                const x = c.x + (off ? off[0] : 0);
                const y = c.y + (off ? off[1] : 0);
                const isActive = hovered === loc.id || selected === loc.id;
                return (
                  <g key={`label-${loc.id}`}>
                    {isExternal && (
                      <rect
                        x={x - 11}
                        y={y - 8}
                        width={22}
                        height={16}
                        fill="transparent"
                        role="button"
                        tabIndex={0}
                        aria-label={`Find providers in ${loc.name}`}
                        onMouseEnter={() => setHovered(loc.id)}
                        onMouseLeave={() => setHovered((h) => (h === loc.id ? null : h))}
                        onFocus={() => setHovered(loc.id)}
                        onBlur={() => setHovered((h) => (h === loc.id ? null : h))}
                        onClick={() => goToState({ id: loc.id, name: loc.name })}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            goToState({ id: loc.id, name: loc.name });
                          }
                        }}
                        style={{ cursor: "pointer", outline: "none" }}
                      />
                    )}
                    <text
                      x={x}
                      y={y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      pointerEvents="none"
                      style={{
                        fontSize: isExternal ? 10 : 11,
                        fontWeight: 600,
                        fontFamily: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
                        fill: isActive
                          ? "var(--color-primary-foreground)"
                          : "color-mix(in oklab, var(--color-foreground) 75%, transparent)",
                        paintOrder: "stroke",
                        stroke: isActive ? "transparent" : "var(--color-background)",
                        strokeWidth: 2.5,
                        strokeLinejoin: "round",
                      }}
                    >
                      {abbr}
                    </text>
                  </g>
                );
              })}
            </svg>

            {hoveredLoc && tooltipPos && (
              <div
                className="pointer-events-none absolute z-30 card-surface text-card-foreground shadow-lg px-3 py-2 text-[12px] whitespace-nowrap -translate-x-1/2 -translate-y-[calc(100%+10px)] rounded-md"
                style={{ left: tooltipPos.x, top: tooltipPos.y }}
              >
                <div className="font-semibold text-foreground text-center">{hoveredLoc.name}</div>
                <div className="text-muted-foreground text-center">Find providers</div>
              </div>
            )}
          </div>
        </div>

        {/* Popular states — CityFinder-style quick links below the map card */}
        <p className="mt-8 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          Popular states
        </p>
        <ul className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 gap-y-3">
          {popular.map((loc) => (
            <li key={loc.id}>
              <button
                type="button"
                onClick={() => goToState(loc)}
                onMouseEnter={() => setHovered(loc.id)}
                onMouseLeave={() => setHovered((h) => (h === loc.id ? null : h))}
                className="inline-flex items-center gap-2 text-[14px] text-muted-foreground hover:text-primary transition-colors"
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                <span className="truncate">{loc.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
